import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiService } from '@quorum/api';
import { LocationQueryParameters } from '@quorum/models/xs-query';
import { Address, Associate, Location, MessageType } from '@quorum/models/xs-resource';
import { ServiceLaneDropdownsFacade } from '@quorum/service-lane-dropdowns/facade';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import { createFormGroupFromObject } from 'libs/sha-services/src/lib/createFormGroupFromObject';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { google } from 'google-maps';
@Component({
  selector: 'svc-vehicle-owner-create-dialog',
  templateUrl: './vehicle-owner-create-dialog.component.html',
  styleUrls: ['./vehicle-owner-create-dialog.component.scss'],
})
export class VehicleOwnerCreateDialogComponent implements OnInit {
  @ViewChild('cellPhoneNumber') cellPhoneNumber: ElementRef;
  addressFormProvStateSub: Subscription;
  filteredLocations$: Observable<Location[]>;
  phoneNumberMask = ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
  assocRequired: boolean = true;
  emailRequired: boolean = false;
  cellPhoneRequired: boolean = false;
  createReady: boolean = false;

  associateForm: FormGroup;
  addressForm: FormGroup;
  associateAddressCreateFormSub: Subscription;
  associateFormNameSub: Subscription;
  associateFormChangesSub: Subscription;
  addressFormChangesSub: Subscription;
  associateFormCombinedSub: Subscription;
  addressLineSub: Subscription;
  addressPredictions: any[];
  placesService: any;

  constructor(
    public dialogRef: MatDialogRef<VehicleOwnerCreateDialogComponent>,

    @Inject(MAT_DIALOG_DATA)
    public data: {
      associateDropdowns$: Observable<any>;
      addressDropdowns$: Observable<any>;
      locations$: Observable<Location[]>;
      currentAssociate$: Observable<Associate>;
      currentAddress$: Observable<Address>;
      typedName: string;
    },
    private apiService: ApiService,
    public serviceLaneFacade: ServiceLaneFacade,
    public serviceLaneDropdownFacade: ServiceLaneDropdownsFacade,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.associateAddressCreateFormSub = combineLatest(this.data.currentAssociate$, this.data.currentAddress$)
      .pipe(
        map(([currentAssociate, currentAddress]) => {
          this.associateForm = createFormGroupFromObject(currentAssociate);
          this.associateForm.addControl('name', new FormControl(''));
          this.associateForm.reset();
          if (this.data.typedName) {
            this.associateForm.get('name').setValue(this.data.typedName);
            this.associateForm
              .get('firstName')
              .setValue(
                this.associateForm.get('name').value.split(',')[1]
                  ? this.associateForm.get('name').value.split(',')[1].trim()
                  : this.associateForm.get('name').value
              );
            this.associateForm
              .get('lastName')
              .setValue(
                this.associateForm.get('name').value.split(',')[1]
                  ? this.associateForm.get('name').value.split(',')[0].trim()
                  : null
              );
          }

          this.setAssociateRequiredFields();
          this.associateForm.markAllAsTouched();
          this.associateForm.get('classId').patchValue('INDIV');

          this.addressForm = createFormGroupFromObject(currentAddress);
          this.addressForm.reset();
          this.setAddressRequiredFields();
          this.addressForm.markAllAsTouched();
        })
      )
      .subscribe();

    this.associateFormNameSub = this.associateForm
      .get('name')
      .valueChanges.pipe(
        map((value: any) => {
          this.associateForm
            .get('firstName')
            .setValue(
              this.associateForm.get('name').value.split(',')[1]
                ? this.associateForm.get('name').value.split(',')[1].trim()
                : this.associateForm.get('name').value
            );
          this.associateForm
            .get('lastName')
            .setValue(
              this.associateForm.get('name').value.split(',')[1]
                ? this.associateForm.get('name').value.split(',')[0].trim()
                : null
            );
        }),
        catchError((error) => {
          this.associateForm.enable({ emitEvent: false });
          return of(error);
        })
      )
      .subscribe();

    this.associateFormCombinedSub = combineLatest(
      this.data.associateDropdowns$,
      this.associateForm.get('messagePreferenceId').valueChanges
    )
      .pipe(
        map(([appointmentDropdowns, messagePreference]) => {
          return { messageTypes: appointmentDropdowns.messageTypes, messagePreference };
        })
      )
      .subscribe((combinedData) => {
        if (
          combinedData.messageTypes
            .find((messageType: MessageType) => messageType.id === combinedData.messagePreference)
            .description.toLowerCase() === 'email'
        ) {
          this.associateForm.get('emailAddress').setValidators([Validators.email, Validators.required]);
          this.associateForm.get('emailAddress').markAsTouched();
          this.associateForm.get('emailAddress').updateValueAndValidity();
          this.addressForm.get('cellPhoneNumber').clearValidators();
          this.addressForm.get('cellPhoneNumber').updateValueAndValidity();
        } else if (
          combinedData.messageTypes
            .find((messageType: MessageType) => messageType.id === combinedData.messagePreference)
            .description.toLowerCase() === 'text'
        ) {
          this.addressForm.get('cellPhoneNumber').setValidators(Validators.required);
          this.addressForm.get('cellPhoneNumber').markAsTouched();
          this.addressForm.get('cellPhoneNumber').updateValueAndValidity();
          this.associateForm.get('emailAddress').setValidators([Validators.email]);
          this.associateForm.get('emailAddress').updateValueAndValidity();
        } else {
          this.associateForm.get('emailAddress').setValidators([Validators.email]);
          this.associateForm.get('emailAddress').updateValueAndValidity();
          this.addressForm.get('cellPhoneNumber').clearValidators();
          this.addressForm.get('cellPhoneNumber').updateValueAndValidity();
        }
      });

    this.associateFormChangesSub = this.associateForm.valueChanges.subscribe((value) => {
      if (
        (this.associateForm.valid || this.associateForm.disabled) &&
        (this.addressForm.valid || this.addressForm.disabled)
      ) {
        this.createReady = true;
      } else {
        this.createReady = false;
      }
    });

    this.addressFormChangesSub = this.addressForm.valueChanges.subscribe((value) => {
      if (
        (this.associateForm.valid || this.associateForm.disabled) &&
        (this.addressForm.valid || this.addressForm.disabled)
      ) {
        this.createReady = true;
      } else {
        this.createReady = false;
      }
    });

    this.addressFormProvStateSub = this.addressForm
      .get('provStateId')
      .valueChanges.pipe(
        tap((provState) => {
          this.addressForm.patchValue({
            locationId: '',
          });
        }),
        switchMap((provState) => {
          let params: LocationQueryParameters = new LocationQueryParameters({
            provStateId: provState,
            pageSize: 5000,
          });
          return this.apiService.get<Location[]>('v/1/associates/locations', { params: params }).pipe(
            switchMap((locations) => {
              this.filteredLocations$ = this.addressForm.get('locationId').valueChanges.pipe(
                startWith(''),
                map((value) => {
                  if (typeof value === 'string') {
                    return locations.filter((option: any) =>
                      option.description.toLowerCase().includes(value.toLowerCase())
                    );
                  } else {
                    return locations.filter((option: any) => option.id === value.id);
                  }
                }),
                tap((locations: Location[]) => {
                  this.addressForm.get('locationId').enable({ emitEvent: false });
                })
              );
              return of(null);
            })
          );
        })
      )
      .subscribe((provState: any) => {});

    this.addressLineSub = this.addressForm
      .get('addressLine1')
      .valueChanges.pipe(
        filter((input: string) => {
          if (input) {
            return input.length >= 4;
          }
        }),
        debounceTime(500),
        distinctUntilChanged(),
        map((value) => {
          this.getAutocompletePredictions(value);
        })
      )
      .subscribe(() => {});
  }

  getAutocompletePredictions(searchedAddressValue: any) {
    let autocompleteService = new google.maps.places.AutocompleteService();

    autocompleteService.getPlacePredictions(
      {
        types: ['address'],
        input: searchedAddressValue,
        componentRestrictions: { country: ['ca', 'us'] },
      },
      (predictions: any, status: any) => {
        if (status == google.maps.places.PlacesServiceStatus.OK) {
          this.addressPredictions = predictions;
        } else {
          console.log(status, 'error in address predictions');
        }
      }
    );
  }

  selectAddress(selectedAddress: any, addressDropdowns: any) {
    let addressField = document.querySelector('#addressLine1') as HTMLInputElement;
    this.placesService = new google.maps.places.PlacesService(addressField);

    this.placesService.getDetails(
      { placeId: selectedAddress.place_id, fields: ['address_components'] },
      (result: any, status: any) => {
        let address: any = {
          addressLine1: '',
          provStateId: '',
          locationName: '',
          postalZipCode: '',
          countryId: '',
          dropdownsLocation: null,
          location: null,
        };

        for (const component of result.address_components) {
          const componentType = component.types[0];

          switch (componentType) {
            case 'country': {
              address.countryId = component.short_name === 'CA' ? 'CAN' : component.short_name === 'US' ? 'USA' : null;

              this.addressForm
                .get('countryId')
                .patchValue(component.short_name === 'CA' ? 'CAN' : component.short_name === 'US' ? 'USA' : null, {
                  emitEvent: false,
                });
              break;
            }

            case 'street_number': {
              address.addressLine1 = `${component.long_name} `;
              break;
            }

            case 'route': {
              address.addressLine1 += component.short_name;
              break;
            }

            case 'locality': {
              address.locationName = component.long_name;
              break;
            }

            case 'postal_code_prefix': {
              address.postalZipCode = component.long_name;
              break;
            }

            case 'postal_code': {
              address.postalZipCode = component.long_name;
              break;
            }

            case 'postal_code_suffix': {
              address.postalZipCode = `${address.postalZipCode}-${component.long_name}`;
              break;
            }

            case 'administrative_area_level_1': {
              this.addressForm.get('locationId').enable({ emitEvent: false });

              this.addressForm
                .get('provStateId')
                .patchValue(component.short_name === 'QC' ? 'PQ' : component.short_name, {
                  emitEvent: false,
                });

              address.provStateId = component.short_name === 'QC' ? 'PQ' : component.short_name;
              break;
            }
          }
        }

        if (address.locationName) {
          let selectedLocation: Location = addressDropdowns.locations.filter((option: Location): any => {
            return (
              option.description.toLowerCase().includes(address.locationName.toLowerCase()) &&
              option.provStateId.toLowerCase().includes(address.provStateId.toLowerCase())
            );
          });
          if (selectedLocation[0]) {
            this.addressForm.get('locationId').patchValue(selectedLocation[0], { emitEvent: false });
            this.addressForm.get('countryId').patchValue(selectedLocation[0].countryId, { emitEvent: false });
            address.dropdownsLocation = selectedLocation[0];
          } else {
            this.addressForm.get('locationId').patchValue(null);
          }
        }

        this.addressForm.get('addressLine1').patchValue(address.addressLine1, { emitEvent: false });
        this.addressForm.get('postalZipCode').patchValue(address.postalZipCode, { emitEvent: false });

        this.cellPhoneNumber.nativeElement.focus();

        if (address.dropdownsLocation) {
          this.addressForm.get('locationId').disable({ emitEvent: false });
        }

        this.addressPredictions = [];
      }
    );
  }

  setAssociateRequiredFields() {
    this.associateForm.get('name').setValidators(Validators.required);
    this.associateForm.get('messagePreferenceId').setValidators(Validators.required);
    this.associateForm.get('emailAddress').setValidators(Validators.email);
  }

  setAddressRequiredFields() {
    this.addressForm.get('addressLine1').setValidators(Validators.required);
    this.addressForm.get('locationId').setValidators(Validators.required);
    this.addressForm.get('provStateId').setValidators(Validators.required);
    this.addressForm.get('locationId').disable({ emitEvent: false, onlySelf: true });
  }

  setCountryCode(provinceState: any) {
    this.addressForm.patchValue({
      countryId: provinceState.countryId,
    });
    this.cdr.detectChanges();
  }

  setPostalCodeAndProvince(location: Location) {
    this.addressForm.patchValue({
      location: location,
      postalZipCode: location.postalCode,
      countryId: location.countryId,
    });
    this.cdr.detectChanges();
  }

  displayLocationName(location: Location): string {
    return location && location.description
      ? `${location.description} ${location.provStateId}, ${location.countryId}`
      : '';
  }

  onSubmit(newAssociateForm: any, newAddressForm: any) {
    this.dialogRef.close({
      newAssociate: newAssociateForm,
      newAddress: newAddressForm,
    });
  }

  ngOnDestroy() {
    this.associateFormChangesSub.unsubscribe();
    this.addressFormChangesSub.unsubscribe();
    this.associateFormNameSub.unsubscribe();
    this.addressFormProvStateSub.unsubscribe();
    this.associateFormCombinedSub.unsubscribe();
    this.associateAddressCreateFormSub.unsubscribe();
    this.addressLineSub.unsubscribe();
  }

  makeUpperCase(str: string) {
    if (str) {
      str = str[0].toUpperCase() + str.slice(1);
      str = str.replace(/,\s*([a-z])/g, function (d, e) {
        return ', ' + e.toUpperCase();
      });
      str = str.replace(/'\s*([a-z])/g, function (d, e) {
        return "'" + e.toUpperCase();
      });
      return str;
    }
    return '';
  }
}
